/**
 * WorkspacePage — enterprise page layout primitive.
 *
 * Standardizes the anatomy of an operational workspace so that every
 * top-level page renders with the same header rhythm, action row
 * placement, primary content slot, and optional secondary insights
 * area below.
 *
 * Anatomy:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │ Page header                                          │
 *   │ • H1 title                                           │
 *   │ • subtitle (optional one-sentence operator orient.)  │
 *   │ • actions slot (right-aligned)                       │
 *   ├──────────────────────────────────────────────────────┤
 *   │ Primary workspace content (children)                 │
 *   ├──────────────────────────────────────────────────────┤
 *   │ Optional secondary insights area (collapses at md)   │
 *   └──────────────────────────────────────────────────────┘
 *
 * This is Milestone 2A — STRUCTURE only. No visual redesign. No
 * decorative chrome. The container provides:
 *   - consistent vertical rhythm via the design-system spacing tokens
 *   - one canonical place for the page H1 and actions
 *   - a `data-testid` on the page root so cross-route navigation tests
 *     can lock that the operator landed in the right workspace
 *
 * Pages NOT yet migrated keep rendering as-is — WorkspacePage is
 * opt-in. Tests cover both the primitive itself and the three
 * proof-of-concept migrations (Today / Executive / Alerts).
 */

import React from 'react';
import './WorkspacePage.css';

// Sprint 3A — adaptive width + density modifier sets.
// Both props are OPT-IN. Passing nothing (or `null` / undefined)
// leaves the modifier class off entirely, preserving the exact
// pre-Sprint-3A default rendering. The modifier classes only kick
// in when a page explicitly chooses a mode.
const VALID_WIDTH_MODES = Object.freeze(['narrative', 'operational']);
const VALID_DENSITY_MODES = Object.freeze(['comfortable', 'dense']);

export default function WorkspacePage({
  title,
  subtitle = null,
  eyebrow = null,
  actions = null,
  secondary = null,
  contextSlot = null,
  className = '',
  // Sprint 3A: width and density opt-in props.
  //   width="narrative"   → clamps content to --container-narrative-max (800px)
  //   width="operational" → clamps content to --container-operational-max (min(100%, 2200px))
  //   omitted / null      → no width modifier; content fills the parent
  //                          exactly as it did before Sprint 3A.
  //   density="comfortable" → default paddings/gaps
  //   density="dense"       → tighter paddings/gaps for table-heavy pages
  //   omitted / null        → no density modifier; current behavior.
  width = null,
  density = null,
  'data-testid': testId = 'workspace-page',
  'data-workspace-id': workspaceId = null,
  children
}) {
  const widthValid = VALID_WIDTH_MODES.includes(width);
  const densityValid = VALID_DENSITY_MODES.includes(density);
  const widthClass = widthValid ? ` workspace-page--width-${width}` : '';
  const densityClass = densityValid ? ` workspace-page--density-${density}` : '';
  const rootClassName = `workspace-page${widthClass}${densityClass}${className ? ` ${className}` : ''}`;
  return (
    <section
      className={rootClassName}
      data-testid={testId}
      data-workspace-id={workspaceId || undefined}
      data-width={widthValid ? width : undefined}
      data-density={densityValid ? density : undefined}
    >
      {contextSlot ? (
        <div className="workspace-page__context" data-testid="workspace-page-context-slot">
          {contextSlot}
        </div>
      ) : null}

      <header className="workspace-page__header">
        <div className="workspace-page__header-text">
          {eyebrow ? (
            <span className="workspace-page__eyebrow" data-testid="workspace-page-eyebrow">
              {eyebrow}
            </span>
          ) : null}
          {title ? (
            <h1 className="workspace-page__title" data-testid="workspace-page-title">
              {title}
            </h1>
          ) : null}
          {subtitle ? (
            <p className="workspace-page__subtitle" data-testid="workspace-page-subtitle">
              {subtitle}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div className="workspace-page__actions" data-testid="workspace-page-actions">
            {actions}
          </div>
        ) : null}
      </header>

      <div className="workspace-page__primary" data-testid="workspace-page-primary">
        {children}
      </div>

      {secondary ? (
        <aside
          className="workspace-page__secondary"
          aria-label="Secondary insights"
          data-testid="workspace-page-secondary"
        >
          {secondary}
        </aside>
      ) : null}
    </section>
  );
}
